/**
 * OMF Stock Availability Widget
 * Uses window.omfGeo (omf-geolocation.js) for all location and shipping logic
 */

class StockAvailabilityWidget {
  constructor() {
    this.widget = document.querySelector('.stock-availability-widget');
    if (!this.widget) return;

    // Core elements
    this.form = this.widget.querySelector('.stock-availability-form');
    this.input = this.widget.querySelector('.stock-availability-input');
    this.button = this.widget.querySelector('.stock-availability-button');
    this.results = this.widget.querySelector('.stock-availability-results');
    this.locationElem = this.widget.querySelector('.stock-availability-location');

    // Suburb dropdown — we'll create this dynamically
    this.suburbDropdown = null;

    this.initializeWidget();
  }


  // ================================================
  // INIT
  // ================================================

  initializeWidget() {
    if (this.results) {
      this.results.style.display = 'none';
    }
    this.setupEventHandlers();
  }

  setupEventHandlers() {
    if (!this.form || !this.input || !this.button) return;

    // On form submit — show suburb dropdown
    this.form.addEventListener('submit', this.handlePostcodeSubmit.bind(this));

    // Also trigger on Enter key
    this.input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.handlePostcodeSubmit(e);
      }
    });

    // Clear errors and dropdown when user changes postcode
    this.input.addEventListener('input', () => {
      this.clearValidationError();
      this.removeSuburbDropdown();
      this.hideResults();
    });
  }

  // ================================================
  // POSTCODE SUBMIT — show suburb dropdown
  // ================================================

  async handlePostcodeSubmit(e) {
    e.preventDefault();

    const postcode = this.input.value.trim();
    if (!postcode) return;

    // Basic format validation
    if (!/^\d{4}$/.test(postcode)) {
      this.showInputError('Please enter a valid 4-digit postcode');
      return;
    }

    // Disable button while loading suburbs
    this.button.disabled = true;
    this.clearValidationError();
    this.removeSuburbDropdown();

    try {
      // Check omfGeo is available
      if (!window.omfGeo) {
        throw new Error('Location service not available. Please refresh and try again.');
      }

      // Get matching suburbs
      const matches = await window.omfGeo.getPostcodeMatches(postcode);

      if (!matches || matches.length === 0) {
        this.showInputError('Postcode not found. Please check and try again.');
        return;
      }

      // Show suburb dropdown
      this.showSuburbDropdown(matches);

    } catch (err) {
      this.showInputError(err.message || 'Something went wrong. Please try again.');
    } finally {
      this.button.disabled = false;
    }
  }

  // ================================================
  // SUBURB DROPDOWN
  // ================================================

  showSuburbDropdown(matches) {
    this.removeSuburbDropdown();

    const dropdown = document.createElement('div');
    dropdown.className = 'suburb-dropdown';
    dropdown.style.cssText = `
      border: 1px solid rgba(var(--color-foreground), 0.1);
  border-radius: 0.4rem;
  margin-top: 0.5rem;
  overflow-y: auto;
  max-height: 18rem;
  background: rgb(var(--color-background));
    `;

    matches.forEach(match => {
      const item = document.createElement('div');
      item.className = 'suburb-dropdown-item';
      item.style.cssText = `
        padding: 1rem 1.2rem;
        cursor: pointer;
        font-size: 1.4rem;
        border-bottom: 1px solid rgba(var(--color-foreground), 0.06);
        display: flex;
        justify-content: space-between;
        align-items: center;
      `;

      // Format: "Suburb Name" with state on the right
      const titleCase = str => str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

      item.innerHTML = `
        <span>${titleCase(match.Suburb)}</span>
        <span style="color: rgba(var(--color-foreground), 0.5); font-size: 1.2rem;">${match.State} ${match.Postocode}</span>
      `;

      // Hover effect
      item.addEventListener('mouseenter', () => {
        item.style.backgroundColor = 'rgba(var(--color-foreground), 0.04)';
      });
      item.addEventListener('mouseleave', () => {
        item.style.backgroundColor = '';
      });

      // On click — run availability check with this suburb's exact coords
      item.addEventListener('click', () => {
        // Update input to show selected suburb
        this.input.value = `${match.Suburb.charAt(0) + match.Suburb.slice(1).toLowerCase()}, ${match.State} ${match.Postocode}`;
        this.removeSuburbDropdown();
        this.runAvailabilityCheck(match);
      });

      dropdown.appendChild(item);
    });

    // Insert after form
    this.form.after(dropdown);
    this.suburbDropdown = dropdown;
  }

  removeSuburbDropdown() {
    if (this.suburbDropdown) {
      this.suburbDropdown.remove();
      this.suburbDropdown = null;
    }
  }

  // ================================================
  // AVAILABILITY CHECK — using selected suburb coords
  // ================================================

  async runAvailabilityCheck(suburb) {
    if (!this.widget || !this.results || !this.button) return;

    this.clearValidationError();
    this.results.classList.remove('error-state');
    this.showLoadingState();

    try {
      if (!window.omfGeo) {
        throw new Error('Location service not available');
      }

      // Use exact coords from selected suburb
      const lat = parseFloat(suburb.Latitude);
      const lng = parseFloat(suburb.Longitude);

      if (isNaN(lat) || isNaN(lng)) {
        throw new Error('Invalid suburb coordinates');
      }

      // Validate Australian longitude (must be positive 113-154)
      // PO Box postcodes have bad coordinate data
      if (lng <= 0) {
        this.showInputError('This appears to be a PO Box postcode. Please enter a street delivery postcode.');
        this.hideLoadingState();
        // Force hide immediately without animation
        this.results.classList.remove('visible');
        this.results.style.display = 'none';
        return;
      }

      // Find nearest store
      const result = await window.omfGeo.getNearestStore(lat, lng);
      if (!result) {
        throw new Error('No stores found');
      }

      // Get shipping tier
      const shipping = window.omfGeo.getShippingTier(result.distance);

      // Update UI
      this.updateUI({
        nearestStore: result.store,
        distance: result.distance,
        shipping,
        valid: true,
      }, suburb);

      this.showResults();

    } catch (err) {
      console.error('OMF Widget: Error checking availability:', err);
      this.showErrorState();
    } finally {
      this.hideLoadingState();
    }
  }

  // ================================================
  // UPDATE UI
  // ================================================

  updateUI(data, suburb) {
    const { nearestStore: store, shipping } = data;

    // Store header
    const storeName = this.results.querySelector('.store-name');
    const storeAddress = this.results.querySelector('.store-address');
    if (storeName) storeName.textContent = `OMF ${store.name}`;
    if (storeAddress) storeAddress.textContent = `${store.address}, ${store.city} ${store.postcode}`.trim();

    // Directions link
    const directionsLink = this.locationElem?.querySelector('.directions-link');
    if (directionsLink) {
      directionsLink.href = store.pageUrl || '/pages/stores';
      directionsLink.style.display = 'inline-flex';
    }

    // Phone number — strip non-digits for tel: href, keep display as-is
    const rawPhone = store.phone || '';
    const telHref = rawPhone ? `tel:${rawPhone.replace(/\D/g, '')}` : '';

    this.widget.querySelectorAll('.phone-display').forEach(el => {
      el.textContent = rawPhone;
    });

    const headerPhoneLink = this.locationElem?.querySelector('.header-phone-link');
    if (headerPhoneLink) {
      headerPhoneLink.href = telHref;
      headerPhoneLink.style.display = rawPhone ? 'inline-flex' : 'none';
    }

    // Delivery cost line
    const deliveryCostLine = this.results.querySelector('.delivery-cost-line');
    if (deliveryCostLine && shipping) {
      deliveryCostLine.textContent = `$${shipping.rate} · ${shipping.label}`;
    }

    // Stock options
    const isOmfVendor = this.widget.dataset.productOmf?.trim() === 'true';

    const suburbName = suburb
      ? suburb.Suburb.charAt(0) + suburb.Suburb.slice(1).toLowerCase()
      : '';
    const suburbPostcode = suburb?.Postocode || '';

    const deliveryText = [suburbName, suburbPostcode].filter(Boolean).join(' ');
    const deliveryAvailableText = deliveryText
      ? `Available to order to ${deliveryText}`
      : 'Available to order';

    this.updateStockOptionAvailability(
      this.widget.querySelector('[data-option-type="try-instore"]'),
      isOmfVendor,
      this.widget.dataset.tryInstoreAvailable || 'Available to try in-store',
      this.widget.dataset.tryInstoreUnavailable || 'Not available to try in-store'
    );

    this.updateStockOptionAvailability(
      this.widget.querySelector('[data-option-type="pickup"]'),
      isOmfVendor,
      'Free pickup available',
      this.widget.dataset.pickupUnavailable || 'Not available for Click & Collect'
    );

    this.updateStockOptionAvailability(
      this.widget.querySelector('[data-option-type="delivery"]'),
      true,
      deliveryAvailableText,
      this.widget.dataset.deliveryUnavailable || 'Not available for delivery in your area'
    );

    this.updateStockOptionAvailability(
      this.widget.querySelector('[data-option-type="delivery-callback"]'),
      true,
      'Our team will call within 1 business day to book your delivery',
      ''
    );

    // Call CTA — only show for OMF products with a phone number
    const callCta = this.results.querySelectorAll('.results-section')[0]?.querySelector('.store-call-cta');
    if (callCta) {
      if (isOmfVendor && rawPhone) {
        callCta.href = telHref;
        callCta.style.display = 'inline-flex';
      } else {
        callCta.style.display = 'none';
      }
    }

    this.forceGreenDots(isOmfVendor, isOmfVendor, true, true);
  }

  updateStockOptionAvailability(option, isAvailable, availableText, unavailableText) {
    if (!option) return;

    const dot = option.querySelector('.stock-option-dot');
    const title = option.querySelector('.stock-option-title');

    if (!dot || !title) return;

    if (isAvailable) {
      dot.classList.add('available');
      dot.style.removeProperty('background-color');
    } else {
      dot.classList.remove('available');
      dot.style.backgroundColor = '#ccc';
    }

    title.textContent = isAvailable ? availableText : unavailableText;
  }

  forceGreenDots(...availability) {
    const dots = this.widget.querySelectorAll('.stock-option-dot');
    availability.forEach((available, i) => {
      if (!dots[i]) return;
      if (available) {
        dots[i].classList.add('available');
        dots[i].style.removeProperty('background-color');
      } else {
        dots[i].classList.remove('available');
        dots[i].style.backgroundColor = '#ccc';
      }
    });
  }

  // ================================================
  // ERROR STATE
  // ================================================

  showErrorState() {
    if (!this.results) return;

    this.results.classList.add('error-state');

    const storeName = this.results.querySelector('.store-name');
    if (storeName) storeName.textContent = 'Error checking availability';

    this.locationElem?.querySelector('.directions-link')?.style.setProperty('display', 'none');
    this.locationElem?.querySelector('.header-phone-link')?.style.setProperty('display', 'none');

    this.widget.querySelectorAll('.stock-option-dot').forEach(dot => {
      dot.classList.remove('available');
    });

    this.showResults();
  }

  // ================================================
  // LOADING STATE
  // ================================================

  showLoadingState() {
    if (this.results) {
      this.results.style.display = 'block';
      this.results.classList.add('visible', 'loading');
      this.results.querySelectorAll('.results-section').forEach(s => s.classList.add('loading'));
      this.results.querySelector('.store-name')?.classList.add('skeleton-loading');
      this.results.querySelector('.store-address')?.classList.add('skeleton-loading');
    }
    if (this.button) this.button.disabled = true;
  }

  hideLoadingState() {
    if (this.results) {
      this.results.classList.remove('loading');
      this.results.querySelectorAll('.results-section').forEach(s => s.classList.remove('loading'));
      this.results.querySelector('.store-name')?.classList.remove('skeleton-loading');
      this.results.querySelector('.store-address')?.classList.remove('skeleton-loading');
    }
    if (this.button) this.button.disabled = false;
  }

  // ================================================
  // SHOW / HIDE RESULTS
  // ================================================

  showResults() {
    if (!this.results) return;
    this.results.style.display = 'block';
    setTimeout(() => {
      this.results.classList.add('visible');
    }, 10);
  }

  hideResults() {
    if (!this.results) return;
    this.results.classList.remove('visible');
    setTimeout(() => {
      if (!this.results.classList.contains('visible')) {
        this.results.style.display = 'none';
      }
    }, 300);
  }

  // ================================================
  // VALIDATION ERRORS
  // ================================================

  showInputError(message) {
    this.clearValidationError();

    const errorElement = document.createElement('div');
    errorElement.className = 'validation-error-message';
    errorElement.textContent = message;
    errorElement.style.cssText = `
      color: #d32f2f;
      font-size: 14px;
      margin-top: 8px;
      padding: 8px 12px;
      background-color: #ffebee;
      border: 1px solid #ffcdd2;
      border-radius: 4px;
    `;

    if (this.form) {
      this.form.after(errorElement);
    }
  }

  clearValidationError() {
    const existingError = this.widget.querySelector('.validation-error-message');
    if (existingError) existingError.remove();
  }
}

// ================================================
// INIT
// ================================================

document.addEventListener('DOMContentLoaded', () => {
  new StockAvailabilityWidget();
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.stock-availability-widget')) {
    new StockAvailabilityWidget();
  }
});